/*
 * <shared-homepage-slideshow>
 *
 * Mirrors the homepage slideshow onto another template (e.g. collection pages)
 * using Shopify's Section Rendering API:
 *   https://shopify.dev/docs/api/ajax/section-rendering
 *
 *
 * After injection we re-run any inline scripts that came along (their content
 * does not execute when set via innerHTML). The `<slideshow-component>` custom
 * element is registered globally in assets/global.js, so it auto-upgrades as
 * soon as the markup lands in the DOM.
 */

// v2 — earlier versions cached the announcement bar's section id by mistake.
// Bumping the key invalidates any stale entries on the next page load.
const SECTION_ID_CACHE_KEY = 'rikumo:homepageSlideshowSectionId:v2';

class SharedHomepageSlideshow extends HTMLElement {
  constructor() {
    super();
    this.homepageUrl = (this.dataset.homepageUrl || '/').replace(/\/+$/, '/') || '/';
    this.explicitSectionId = this.normalizeSectionId(this.dataset.sectionId);
    this.loaded = false;
  }

  connectedCallback() {
    if (this.loaded) return;
    this.loaded = true;
    this.dataset.state = 'loading';
    this.load()
      .then(() => {
        this.dataset.state = 'loaded';
      })
      .catch((err) => {
        console.error('[shared-homepage-slideshow] load failed', err);
        this.dataset.state = 'failed';
        this.classList.add('shared-homepage-slideshow--failed');
      });
  }

  normalizeSectionId(raw) {
    if (!raw) return '';
    // Tolerate merchants pasting the full DOM id from devtools.
    return String(raw).trim().replace(/^#?shopify-section-/, '');
  }

  async load() {
    // Resolve the section id (explicit > cached > discovered). Discovery
    // fetches the full homepage HTML, which inside the theme editor renders
    // with request.design_mode == true — that bypasses slide scheduling.
    // So we ONLY use discovery to obtain the section id; the actual markup
    // we inject must always come from the Section Rendering API endpoint
    // (?section_id=...), which renders with design_mode == false. That way
    // the merchant sees the same filtered view a customer sees.
    let sectionId = this.explicitSectionId || this.readCachedSectionId();

    if (sectionId) {
      const html = await this.fetchSection(sectionId);
      if (html && this.looksLikeHomepageSlideshow(html)) {
        this.inject(html);
        return;
      }
      // Stale id (renamed/removed) or wrong section (announcement bar from
      // an earlier version of this script). Drop it and re-discover.
      this.clearCachedSectionId();
      sectionId = '';
    }

    const discovered = await this.discoverFromHomepage();
    if (!discovered) {
      throw new Error('No slideshow section found on homepage');
    }
    this.writeCachedSectionId(discovered.sectionId);

    const html = await this.fetchSection(discovered.sectionId);
    if (html && this.looksLikeHomepageSlideshow(html)) {
      this.inject(html);
    } else {
      throw new Error('Slideshow section did not return expected markup');
    }
  }

  looksLikeHomepageSlideshow(html) {
    if (!html) return false;
    // Parse rather than regex — class order is fragile, and the announcement
    // bar emits `slideshow__slide` which can confuse word-boundary checks.
    const doc = new DOMParser().parseFromString(html, 'text/html');
    if (doc.querySelector('.announcement-bar-slider')) return false;
    if (doc.querySelector('.announcement-bar')) return false;
    return doc.querySelector('.slideshow.banner') !== null;
  }

  async fetchSection(sectionId) {
    const url = this.buildSectionUrl(sectionId);
    const resp = await fetch(url, { credentials: 'same-origin' });
    if (!resp.ok) return '';
    const text = (await resp.text()).trim();
    // Section Rendering API returns the section markup; treat empty body as "not found".
    if (!text) return '';
    return text;
  }

  async discoverFromHomepage() {
    const resp = await fetch(this.homepageUrl, { credentials: 'same-origin' });
    if (!resp.ok) return null;
    const text = await resp.text();
    const doc = new DOMParser().parseFromString(text, 'text/html');

    // The homepage slideshow section emits a `.slideshow.banner` element;
    // the announcement bar emits `.announcement-bar-slider`. Both are wrapped
    // in <slideshow-component>, so we have to discriminate on inner markup.
    // Walk every section wrapper, pick the first that looks like the banner
    // slideshow and does NOT look like an announcement bar.
    const wrappers = Array.from(doc.querySelectorAll('[id^="shopify-section-"]'));
    const wrapper = wrappers.find(
      (node) =>
        node.querySelector('.slideshow.banner') &&
        !node.querySelector('.announcement-bar-slider') &&
        !node.querySelector('.announcement-bar')
    );

    if (!wrapper) return null;
    const sectionId = wrapper.id.replace(/^shopify-section-/, '');
    return { sectionId, html: wrapper.outerHTML };
  }

  buildSectionUrl(sectionId) {
    const base = this.homepageUrl || '/';
    const sep = base.includes('?') ? '&' : '?';
    return `${base}${sep}section_id=${encodeURIComponent(sectionId)}`;
  }

  inject(html) {
    const template = document.createElement('template');
    template.innerHTML = html.trim();
    const fragment = template.content;

    // Replace the placeholder body. Keep the host element so its data-* attrs
    // remain available for re-runs.
    this.replaceChildren(fragment);

    // innerHTML doesn't execute <script> tags — clone them to run.
    this.activateScripts();
  }

  activateScripts() {
    const scripts = this.querySelectorAll('script');
    scripts.forEach((oldScript) => {
      const newScript = document.createElement('script');
      for (const attr of oldScript.attributes) {
        newScript.setAttribute(attr.name, attr.value);
      }
      if (oldScript.textContent) newScript.textContent = oldScript.textContent;
      oldScript.parentNode.replaceChild(newScript, oldScript);
    });
  }

  readCachedSectionId() {
    try {
      return sessionStorage.getItem(SECTION_ID_CACHE_KEY) || '';
    } catch (_) {
      return '';
    }
  }

  writeCachedSectionId(id) {
    try {
      sessionStorage.setItem(SECTION_ID_CACHE_KEY, id);
    } catch (_) {
      /* ignore quota / private-mode errors */
    }
  }

  clearCachedSectionId() {
    try {
      sessionStorage.removeItem(SECTION_ID_CACHE_KEY);
    } catch (_) {
      /* ignore */
    }
  }
}

if (!customElements.get('shared-homepage-slideshow')) {
  customElements.define('shared-homepage-slideshow', SharedHomepageSlideshow);
}
